import {
  type DepositCheckoutConfig,
  type DepositFlowConfig,
  type DepositMethod,
  type FiatRailConfig,
  getDefaultDynamicTargetAsset,
} from './depositFlowConfig'

type FiatDepositMethod = Extract<DepositMethod, { type: 'apple_pay' | 'card' }>

function isFiatMethod(method: DepositMethod): method is FiatDepositMethod {
  return method.type !== 'crypto'
}

function fiatMethodFromRail(
  paymentMethod: string,
): FiatDepositMethod | undefined {
  if (paymentMethod === 'apple_pay' || paymentMethod === 'card') {
    return { type: paymentMethod }
  }
  return undefined
}

/** The checkout target selected when a deposit first opens. */
export function resolveInitialDepositCheckoutConfig(
  config: DepositFlowConfig,
): DepositCheckoutConfig | undefined {
  const candidate = getDefaultDynamicTargetAsset(
    config.dynamicTargetAssetCandidates,
    config.dynamicRoutingId,
  )
  if (candidate === undefined) {
    return config.checkoutConfig
  }
  return {
    ...config.checkoutConfig,
    targetChain: candidate.tokenChainId,
    targetAsset: candidate.tokenAddress,
  }
}

/**
 * The methods a config offers, with the older shapes mapped onto the list: a
 * deprecated `amountEntry.fiat` names its one rail, and no opt-in at all
 * leaves the V1 list to Add money.
 */
export function resolveDepositMethods(
  config: DepositFlowConfig,
): DepositMethod[] | undefined {
  if (config.methods !== undefined) {
    return config.methods
  }
  const legacyRail = config.amountEntry?.fiat
  if (legacyRail === undefined) {
    return undefined
  }
  const method = fiatMethodFromRail(legacyRail.paymentMethod)
  return method === undefined ? undefined : [method, { type: 'crypto' }]
}

/** Whether the deposit was configured to quote a fiat rail. */
export function hasFiatMethod(config: DepositFlowConfig): boolean {
  if (config.methods !== undefined) {
    return config.methods.some(isFiatMethod)
  }
  return config.amountEntry?.fiat !== undefined
}

/**
 * The fiat rail behind the amount screen, or `undefined` when none is
 * configured. An explicit method list wins over deprecated configuration. Its
 * first fiat method supplies the target ticker, while the active checkout
 * config supplies its address and chain.
 */
export function resolveFiatRail(
  config: DepositFlowConfig,
  checkoutConfig: DepositCheckoutConfig | undefined = config.checkoutConfig,
): FiatRailConfig | undefined {
  if (config.methods !== undefined) {
    const method = config.methods.find(isFiatMethod)
    if (
      method === undefined ||
      method.fiatTargetAssetTicker === undefined ||
      checkoutConfig === undefined
    ) {
      return undefined
    }
    return {
      paymentMethod: method.type,
      cryptoCurrencyCode: method.fiatTargetAssetTicker,
      cryptoCurrencyAddress: checkoutConfig.targetAsset,
      chainId: checkoutConfig.targetChain,
      ...(method.unstable_routingOverrides === undefined
        ? {}
        : { unstable_routingOverrides: method.unstable_routingOverrides }),
    }
  }
  return config.amountEntry?.fiat
}

/** A method list that leaves `crypto` out drops the Crypto row from Add money. */
export function omitsCryptoDeposit(
  methods: DepositMethod[] | undefined,
): boolean {
  return methods !== undefined && !methods.some((m) => m.type === 'crypto')
}
